/**
 * Aviso de fin de descanso (Req. 12.3, 12.7; design.md, 8.3).
 *
 * Dos formas, según lo que ella elija:
 *
 * 1. **Con la app a la vista** (siempre): tres tonos de 880 Hz en el reloj del
 *    `AudioContext`. El mismo toque que marca la serie desbloquea el audio
 *    (iOS solo deja sonar lo que empezó con un toque). La sesión es "ambient":
 *    se mezcla con la música y respeta el modo silencio (Req. 12.7). El reloj
 *    del audio se detiene mientras iOS suspende la app, así que al pasar a
 *    segundo plano se quitan los tonos y al volver se reprograman con la hora
 *    real; si el descanso ya venció, no suena tarde.
 * 2. **Con la pantalla bloqueada** (opcional, 0.12.0): un archivo de audio
 *    hecho al momento, con el silencio que falta y los tres tonos al final.
 *    Es reproducción de verdad, así que iOS la deja seguir en segundo plano y
 *    los tonos suenan con el iPhone bloqueado. A cambio, la sesión pasa a
 *    "playback": pausa la música y suena aunque el teléfono esté en silencio.
 *    Por eso está apagada de forma predeterminada y se enciende en Ajustes.
 *
 * No hay una tercera forma: Safari no tiene notificaciones locales
 * programadas (ver `avisos.ts`), así que sin audio no hay aviso con la app
 * suspendida.
 */

let contexto: AudioContext | null = null;
let programados: OscillatorNode[] = [];
/** Hora de fin (ms) del descanso pendiente de avisar. */
let fin: number | null = null;
let escuchandoPantalla = false;
/** La opción de Ajustes: seguir avisando con la pantalla bloqueada. */
let modoBloqueada = false;

interface SesionAudio {
  type: string;
}

// ---------------------------------------------------------------------------
// Estado real, para que la pantalla no prometa lo que no está pasando
// ---------------------------------------------------------------------------

export type EstadoAviso =
  /** La opción está apagada: suena solo con la app a la vista. */
  | { tipo: 'a_la_vista' }
  | { tipo: 'sin_soporte' }
  /** Falta un toque para que iOS deje reproducir. */
  | { tipo: 'falta_toque' }
  /** El audio va corriendo: el aviso llega aunque se bloquee la pantalla. */
  | { tipo: 'listo' }
  /** El sistema lo pausó (otra app tomó el audio, por ejemplo). */
  | { tipo: 'pausado' };

let estado: EstadoAviso = { tipo: 'a_la_vista' };
const oyentes = new Set<(e: EstadoAviso) => void>();

function anunciar(nuevo: EstadoAviso): void {
  if (nuevo.tipo === estado.tipo) return;
  estado = nuevo;
  for (const o of oyentes) o(estado);
}

export function estadoDelAviso(): EstadoAviso {
  return estado;
}

export function escucharEstadoDelAviso(oyente: (e: EstadoAviso) => void): () => void {
  oyentes.add(oyente);
  return () => oyentes.delete(oyente);
}

// ---------------------------------------------------------------------------
// Forma 1: tonos en el reloj del audio, con la app a la vista
// ---------------------------------------------------------------------------

function crearContexto(): AudioContext | null {
  const Clase =
    globalThis.AudioContext ?? (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (Clase === undefined) return null;
  try {
    return new Clase();
  } catch {
    return null;
  }
}

function fijarSesion(tipo: 'ambient' | 'playback'): void {
  const sesion = (navigator as unknown as { audioSession?: SesionAudio }).audioSession;
  if (sesion === undefined) return;
  try {
    sesion.type = tipo;
  } catch {
    // Navegador sin sesión de audio configurable
  }
}

/** Llamar dentro de un toque: prepara el audio para poder sonar después. */
export function prepararSonido(): void {
  fijarSesion(modoBloqueada ? 'playback' : 'ambient');
  if (contexto === null) {
    contexto = crearContexto();
    // Al reanudarse (o al interrumpirlo iOS) se vuelve a calcular con la hora real
    contexto?.addEventListener('statechange', reprogramar);
  }
  if (contexto === null) {
    reprogramar();
    return;
  }
  // Un aviso que esperaba al audio se programa en cuanto corre
  if (contexto.state === 'running') reprogramar();
  else void contexto.resume().then(reprogramar, () => reprogramar());
}

function detenerTonos(): void {
  for (const o of programados) {
    try {
      o.stop();
      o.disconnect();
    } catch {
      // Ya había sonado
    }
  }
  programados = [];
}

function aLaVista(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

/** Pone los tonos en el reloj del audio a partir de la hora real. */
function programarTonos(faltan: number): void {
  if (contexto === null || contexto.state !== 'running' || !aLaVista()) return;
  const inicio = contexto.currentTime + faltan;
  for (let i = 0; i < 3; i++) {
    const o = contexto.createOscillator();
    const volumen = contexto.createGain();
    o.type = 'sine';
    o.frequency.value = 880;
    const t = inicio + i * 0.28;
    volumen.gain.setValueAtTime(0.0001, t);
    volumen.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
    volumen.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
    o.connect(volumen).connect(contexto.destination);
    o.start(t);
    o.stop(t + 0.2);
    programados.push(o);
  }
}

// ---------------------------------------------------------------------------
// Forma 2: un archivo con el silencio que falta y los tonos al final
// ---------------------------------------------------------------------------

/** 8 kHz mono de 8 bits: 90 s pesan 720 KB y el tono de 880 Hz cabe de sobra. */
const MUESTREO = 8000;
const TONO_HZ = 880;
const DURACION_TONO = 0.18;
const SEPARACION_TONO = 0.28;

function escribirTexto(vista: DataView, posicion: number, texto: string): void {
  for (let i = 0; i < texto.length; i++) vista.setUint8(posicion + i, texto.charCodeAt(i));
}

/** Cabecera WAV de PCM de 8 bits sin signo, un canal. */
function encabezadoWav(bytesDeDatos: number): ArrayBuffer {
  const buffer = new ArrayBuffer(44);
  const v = new DataView(buffer);
  escribirTexto(v, 0, 'RIFF');
  v.setUint32(4, 36 + bytesDeDatos, true);
  escribirTexto(v, 8, 'WAVE');
  escribirTexto(v, 12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, MUESTREO, true);
  v.setUint32(28, MUESTREO, true); // bytes por segundo
  v.setUint16(32, 1, true); // bytes por bloque
  v.setUint16(34, 8, true); // bits por muestra
  escribirTexto(v, 36, 'data');
  v.setUint32(40, bytesDeDatos, true);
  return buffer;
}

/**
 * El archivo del aviso: `segundosDeSilencio` de silencio y luego los tres
 * tonos. Con envolvente en los extremos, para que no truenen.
 */
function archivoDeAviso(segundosDeSilencio: number): Blob {
  const colaSeg = SEPARACION_TONO * 2 + DURACION_TONO + 0.1;
  const total = Math.ceil((segundosDeSilencio + colaSeg) * MUESTREO);
  // 128 es el cero del PCM de 8 bits sin signo
  const datos = new Uint8Array(total).fill(128);
  const base = Math.floor(segundosDeSilencio * MUESTREO);
  const muestrasDelTono = Math.floor(DURACION_TONO * MUESTREO);
  for (let n = 0; n < 3; n++) {
    const desde = base + Math.floor(n * SEPARACION_TONO * MUESTREO);
    for (let i = 0; i < muestrasDelTono; i++) {
      const t = i / MUESTREO;
      const envolvente = Math.max(0, Math.min(1, t / 0.01, (DURACION_TONO - t) / 0.03));
      const muestra = Math.sin(2 * Math.PI * TONO_HZ * t) * 110 * envolvente;
      const posicion = desde + i;
      if (posicion < total) datos[posicion] = 128 + Math.round(muestra);
    }
  }
  return new Blob([encabezadoWav(datos.length), datos], { type: 'audio/wav' });
}

let elemento: HTMLAudioElement | null = null;
let urlDelAviso: string | null = null;

function detenerElemento(): void {
  if (elemento !== null) {
    try {
      elemento.pause();
    } catch {
      // Ya estaba detenido
    }
  }
  if (urlDelAviso !== null) {
    URL.revokeObjectURL(urlDelAviso);
    urlDelAviso = null;
  }
}

/**
 * Reproduce el archivo del aviso. Como es audio de verdad, iOS lo deja seguir
 * con la pantalla bloqueada; mientras suena, la app sigue despierta y el
 * banner del sistema también puede salir.
 */
function reproducirArchivo(faltan: number): void {
  if (typeof Audio === 'undefined') {
    anunciar({ tipo: 'sin_soporte' });
    return;
  }
  detenerElemento();
  if (elemento === null) {
    elemento = new Audio();
    elemento.preload = 'auto';
    (elemento as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
    elemento.addEventListener('pause', () => {
      // Al terminar de sonar no es una pausa del sistema: el aviso ya se dio
      if (fin !== null && modoBloqueada && Date.now() < fin) anunciar({ tipo: 'pausado' });
    });
    elemento.addEventListener('playing', () => anunciar({ tipo: 'listo' }));
  }
  urlDelAviso = URL.createObjectURL(archivoDeAviso(faltan));
  elemento.src = urlDelAviso;
  elemento.currentTime = 0;
  void elemento.play().then(
    () => anunciar({ tipo: 'listo' }),
    () => anunciar({ tipo: 'falta_toque' }),
  );
}

// ---------------------------------------------------------------------------

/** Vuelve a calcular el aviso con la hora real, por la forma que toque. */
function reprogramar(): void {
  detenerTonos();
  if (fin === null) {
    detenerElemento();
    anunciar(modoBloqueada ? { tipo: 'falta_toque' } : { tipo: 'a_la_vista' });
    return;
  }
  const faltan = (fin - Date.now()) / 1000;
  if (faltan <= 0) {
    fin = null;
    detenerElemento();
    return;
  }
  if (modoBloqueada) {
    reproducirArchivo(faltan);
    return;
  }
  detenerElemento();
  anunciar({ tipo: 'a_la_vista' });
  programarTonos(faltan);
}

/**
 * El aviso para la hora de fin guardada (ISO); null lo cancela. Si el audio
 * todavía no se desbloquea, queda pendiente hasta el siguiente toque. Sin
 * audio disponible, no hace nada.
 */
export function programarFinDeDescanso(terminaEn: string | null): void {
  const ms = terminaEn === null ? Number.NaN : Date.parse(terminaEn);
  fin = Number.isFinite(ms) ? ms : null;
  if (!escuchandoPantalla && typeof document !== 'undefined') {
    // Al ocultarse se quitan los tonos; al volver, se reprograman o se olvidan.
    // Con la pantalla bloqueada el archivo sigue sonando: no hay nada que rehacer
    document.addEventListener('visibilitychange', () => {
      if (!modoBloqueada || fin === null) reprogramar();
    });
    escuchandoPantalla = true;
  }
  reprogramar();
}

/**
 * Enciende o apaga el aviso con la pantalla bloqueada. Cambiarlo a media
 * sesión vuelve a armar el aviso que esté pendiente.
 */
export function fijarAvisoConPantallaBloqueada(activo: boolean): void {
  if (modoBloqueada === activo) return;
  modoBloqueada = activo;
  fijarSesion(activo ? 'playback' : 'ambient');
  reprogramar();
}

/** Quita el aviso pendiente, por ejemplo al salir del entrenamiento. */
export function cancelarPitido(): void {
  fin = null;
  detenerTonos();
  detenerElemento();
}
